import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Pencil } from 'lucide-react';
import { getLead, updateLead } from '@/lib/api';
import { toast } from 'sonner';

const fmtDate = (v) => {
  if (!v) return '-';
  try {
    const d = new Date(v + 'T00:00:00');
    const day = d.toLocaleDateString('en-US', { weekday: 'short' });
    return `${day}, ${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return v; }
};

const STRENGTH_LABELS = { 1: 'Low', 2: 'Medium', 3: 'High' };
const STRENGTH_COLORS = { 1: 'bg-gray-100 text-gray-600', 2: 'bg-yellow-100 text-yellow-700', 3: 'bg-emerald-100 text-emerald-700' };

export function LeadDetailModal({ leadId, open, onClose }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!open || !leadId) return;
    setLead(null);
    setEditing(false);
    setLoading(true);
    getLead(leadId)
      .then(d => { setLead(d); setForm({...d}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, leadId]);

  const confirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      await updateLead(leadId, form);
      toast.success('Lead updated');
      setEditing(false);
      const updated = await getLead(leadId);
      setLead(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={() => { setEditing(false); onClose(); }}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="lead-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead?.name || 'Lead Details'}</DialogTitle>
          <DialogDescription>
            {lead?.source ? `Source: ${lead.source}` : 'Prospective tenant details'}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-muted-foreground text-sm">Loading...</div>}

        {lead && !loading && (
          <div className="space-y-4">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">Lead</Badge>
              {lead.lead_strength && (
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${STRENGTH_COLORS[lead.lead_strength] || STRENGTH_COLORS[1]}`}>
                  {STRENGTH_LABELS[lead.lead_strength] || 'Low'} Interest
                </span>
              )}
              {lead.converted_to_tenant && <Badge variant="default" className="bg-emerald-600">Converted</Badge>}
            </div>

            {/* Desired Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Desired Move-In</p>
                <p className="text-sm tabular-nums">{fmtDate(lead.desired_start_date)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Desired Move-Out</p>
                <p className="text-sm tabular-nums">{fmtDate(lead.desired_end_date)}</p>
              </div>
            </div>

            <Separator />

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Phone</p>
                <p className="text-sm">{lead.phone || '-'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Email</p>
                <p className="text-sm break-all">{lead.email || '-'}</p>
              </div>
            </div>

            {lead.preferred_contact_method && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Preferred Contact</p>
                <p className="text-sm">{lead.preferred_contact_method}</p>
              </div>
            )}

            <Separator />

            {/* Financial */}
            {lead.price_offered != null && lead.price_offered !== '' && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Price Offered</p>
                <p className="text-sm tabular-nums font-medium">${parseFloat(lead.price_offered).toLocaleString()}</p>
              </div>
            )}

            {/* Extras */}
            {(lead.pets || lead.parking_request || lead.notes || lead.unassigned_note) && (
              <>
                <Separator />
                {lead.pets && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pets</p>
                    <p className="text-sm">{lead.pets}</p>
                  </div>
                )}
                {lead.parking_request && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Parking Request</p>
                    <p className="text-sm">{lead.parking_request}</p>
                  </div>
                )}
                {lead.showing_date && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Showing Date</p>
                    <p className="text-sm">{fmtDate(lead.showing_date)}</p>
                  </div>
                )}
                {lead.notes && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{lead.notes}</p>
                  </div>
                )}
                {lead.unassigned_note && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Unassigned Note</p>
                    <p className="text-sm whitespace-pre-wrap">{lead.unassigned_note}</p>
                  </div>
                )}
              </>
            )}

            {/* Edit inline fields */}
            {editing && (
              <>
                <Separator />
                <div className="space-y-2 pt-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Edit Fields</p>
                  {[
                    { key: 'name', label: 'Name', type: 'text' },
                    { key: 'phone', label: 'Phone', type: 'text' },
                    { key: 'email', label: 'Email', type: 'text' },
                    { key: 'desired_start_date', label: 'Desired Move-In', type: 'date' },
                    { key: 'desired_end_date', label: 'Desired Move-Out', type: 'date' },
                    { key: 'price_offered', label: 'Price Offered', type: 'number' },
                    { key: 'notes', label: 'Notes', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <Label className="text-xs text-muted-foreground">{f.label}</Label>
                      <Input
                        type={f.type}
                        value={form[f.key] || ''}
                        onChange={e => setForm(p => ({...p, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value }))}
                        className="h-7 text-sm mt-0.5"
                        data-testid={`lead-modal-edit-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <Separator className="mt-2" />
        <DialogFooter className="flex-row gap-2 sm:justify-end pt-2">
          {editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => { setForm({...lead}); setEditing(false); }}>Cancel</Button>
              <Button size="sm" onClick={() => setShowConfirm(true)} disabled={saving} data-testid="lead-modal-save">{saving ? 'Saving...' : 'Save'}</Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5" data-testid="lead-modal-edit">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Save Changes?</AlertDialogTitle><AlertDialogDescription>Are you sure you want to save the changes to {lead?.name}?</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmSave} data-testid="lead-modal-confirm-save">Save</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
